function letoileElements(root, selector) {
  const elements = [];

  if (root instanceof Element && root.matches(selector)) {
    elements.push(root);
  }

  root.querySelectorAll?.(selector).forEach((element) => elements.push(element));
  return elements;
}

function letoileStorage() {
  try {
    const storage = window.localStorage;
    const testKey = 'letoile:storage-test';
    storage.setItem(testKey, '1');
    storage.removeItem(testKey);
    return storage;
  } catch (_error) {
    return null;
  }
}

function letoileWholeMoney(value) {
  const text = String(value || '').trim();
  if (!text) return '';

  const currency = /\bQAR\b/i.test(text) ? 'QAR' : 'AED';
  let numberText = text
    .replace(/Dhs\.?/gi, '')
    .replace(/\b(AED|QAR)\b/gi, '')
    .replace(/\s/g, '')
    .trim();
  const separator = Math.max(numberText.lastIndexOf('.'), numberText.lastIndexOf(','));
  const decimalLength = separator >= 0 ? numberText.length - separator - 1 : 0;
  if (separator >= 0 && decimalLength > 0 && decimalLength <= 2) {
    const whole = numberText.slice(0, separator).replace(/[.,]/g, '');
    const decimal = numberText.slice(separator + 1).replace(/[.,]/g, '');
    numberText = `${whole}.${decimal}`;
  } else {
    numberText = numberText.replace(/[.,]/g, '');
  }
  const amount = Number.parseFloat(numberText);
  if (!Number.isFinite(amount)) return text.replace(/([.,]00)(?=\s|$)/, '');

  return `${Math.round(amount).toLocaleString(document.documentElement.lang || 'en', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    useGrouping: true,
  }).replace(/[,٬]/g, ' ')} ${currency}`;
}

class LetoileMobileHistoryBack {
  constructor() {
    this.onClick = this.onClick.bind(this);
    document.addEventListener('click', this.onClick);
  }

  onClick(event) {
    const link = event.target.closest?.('[data-mobile-history-back]');
    if (
      !link
      || event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
      || window.history.length <= 1
    ) {
      return;
    }

    event.preventDefault();
    window.history.back();
  }
}

new LetoileMobileHistoryBack();

class LetoileIntegrationSlots {
  constructor() {
    this.seen = new WeakSet();
    this.slots = [];
    window.LetoileIntegrationSlots = this;
    this.bind();
    this.scan(document);
  }

  bind() {
    document.addEventListener('shopify:section:load', (event) => this.scan(event.target));
  }

  scan(root) {
    letoileElements(root, '[data-integration-slot]').forEach((element) => {
      if (this.seen.has(element)) return;
      this.seen.add(element);
      this.slots.push(element);
      this.notify(this.detailFor(element));
    });
  }

  detailFor(element) {
    return {
      element,
      slot: element.getAttribute('data-integration-slot') || '',
      state: element.getAttribute('data-integration-slot-state') || '',
      sectionId: element.getAttribute('data-integration-section-id') || '',
      trackingHandle: element.getAttribute('data-tracking-handle') || '',
      mode: element.getAttribute('data-cookie-consent-mode') || element.getAttribute('data-wishlist-sync-mode') || '',
    };
  }

  notify(detail) {
    document.dispatchEvent(new CustomEvent('letoile:integration-slot:ready', { detail }));

    const adapter = window.LetoileIntegrationSlotReady;
    if (typeof adapter === 'function') {
      adapter(detail);
    } else if (typeof adapter?.ready === 'function') {
      adapter.ready(detail);
    }
  }
}

new LetoileIntegrationSlots();

function letoileSyncCountTargets(selector, count, kind) {
  document.querySelectorAll(selector).forEach((target) => {
    let badge = target.querySelector(`.site-header__action-count--${kind}, .mobile-bottom-nav__count--${kind}`);
    if (count <= 0) {
      badge?.remove();
      target.removeAttribute('data-count-raw');
      if (target.dataset.countDefaultLabel) target.ariaLabel = target.dataset.countDefaultLabel;
      return;
    }
    if (!badge) {
      badge = document.createElement('span');
      const mobile = target.hasAttribute('data-mobile-bottom-nav-link')
        || target.hasAttribute('data-mobile-wishlist-count-target');
      badge.className = mobile
        ? `mobile-bottom-nav__count mobile-bottom-nav__count--${kind}`
        : `site-header__action-count site-header__action-count--${kind}`;
      badge.ariaHidden = 'true';
      target.append(badge);
    }
    badge.textContent = count > 99 ? '99+' : count;
    target.dataset.countRaw = count;
    if (target.dataset.countLabelTemplate) {
      target.ariaLabel = target.dataset.countLabelTemplate.replace('__count__', count);
    }
  });
}

window.LetoileCountTargets = letoileSyncCountTargets;

class LetoileWishlist {
  constructor(storage = letoileStorage()) {
    this.storage = storage;
    this.storageKey = 'letoile:wishlist';
    this.items = new Map(this.read().map((item) => [item.handle, item]));
    this.bind();
    this.sync();
    this.renderPage();
    window.LetoileWishlist = this;
    this.notify({ action: 'ready' });
  }

  read() {
    if (!this.storage) return [];

    try {
      const raw = JSON.parse(this.storage.getItem(this.storageKey) || '[]');
      return raw
        .map((item) => (typeof item === 'string' ? { handle: item } : item))
        .filter((item) => item?.handle);
    } catch (_error) {
      return [];
    }
  }

  write() {
    if (!this.storage) return;
    try {
      this.storage.setItem(this.storageKey, JSON.stringify([...this.items.values()]));
    } catch (_error) {
      this.storage = null;
    }
  }

  itemFromButton(button) {
    return {
      productId: button.getAttribute('data-product-id') || '',
      variantId: button.getAttribute('data-variant-id') || '',
      handle: button.getAttribute('data-product-handle'),
      title: button.getAttribute('data-product-title') || button.getAttribute('data-product-handle'),
      url: button.getAttribute('data-product-url') || `/products/${button.getAttribute('data-product-handle')}`,
      image: button.getAttribute('data-product-image') || '',
      brand: button.getAttribute('data-product-brand') || '',
      volume: button.getAttribute('data-product-volume') || '',
      price: button.getAttribute('data-product-price') || '',
      compareAtPrice: button.getAttribute('data-product-compare-at-price') || '',
    };
  }

  bind() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-wishlist-toggle]');
      if (!button) return;

      const item = this.itemFromButton(button);
      if (!item.handle) return;

      let action;
      let eventItem = item;
      if (this.items.has(item.handle)) {
        eventItem = this.items.get(item.handle) || item;
        this.items.delete(item.handle);
        action = 'remove';
      } else {
        this.items.set(item.handle, item);
        action = 'add';
      }

      this.write();
      this.sync();
      this.renderPage();
      this.notify({ action, item: eventItem });
    });

    document.addEventListener('shopify:section:load', () => {
      this.sync();
      this.renderPage();
    });

    window.addEventListener('storage', (event) => {
      if (event.key !== this.storageKey) return;
      this.items = new Map(this.read().map((item) => [item.handle, item]));
      this.sync();
      this.renderPage();
      this.notify({ action: 'external-sync' });
    });
  }

  sync() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const handle = button.getAttribute('data-product-handle');
      const active = handle && this.items.has(handle);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      const label = button.getAttribute(active ? 'data-wishlist-remove-label' : 'data-wishlist-add-label');
      if (label) button.setAttribute('aria-label', label);
    });
    this.renderCountTargets();
  }

  renderCountTargets() {
    letoileSyncCountTargets(
      '[data-header-wishlist-count-target], [data-mobile-wishlist-count-target]',
      this.items.size,
      'wishlist',
    );
  }

  productCard(item) {
    const image = item.image
      ? `<span class="product-card__media"><img src="${this.escapeAttribute(item.image)}" alt="${this.escapeAttribute(item.title)}" loading="lazy"></span>`
      : '<span class="product-card__media"></span>';
    const compare = item.compareAtPrice
      ? `<span class="price--compare">${this.escapeHtml(letoileWholeMoney(item.compareAtPrice))}</span>`
      : '';
    const removeLabel = this.removeLabel();
    const removeAriaLabel = removeLabel ? `${removeLabel}: ${item.title}` : item.title;

    return `
      <article class="product-card" data-wishlist-card data-discount-engine-context="product-card" data-discount-engine-product-id="${this.escapeAttribute(item.productId || '')}" data-discount-engine-product-handle="${this.escapeAttribute(item.handle || '')}" data-discount-engine-variant-id="${this.escapeAttribute(item.variantId || '')}">
        <a href="${this.escapeAttribute(item.url)}">${image}</a>
        <button class="wishlist-button" type="button" data-wishlist-toggle data-wishlist-remove data-product-id="${this.escapeAttribute(item.productId || '')}" data-variant-id="${this.escapeAttribute(item.variantId || '')}" data-product-handle="${this.escapeAttribute(item.handle)}" aria-pressed="true" aria-label="${this.escapeAttribute(removeAriaLabel)}">×</button>
        <a class="product-card__title" href="${this.escapeAttribute(item.url)}" data-product-card-identity>${item.brand ? `<span class="product-card__title-brand" data-product-card-brand>${this.escapeHtml(item.brand)}</span> ` : ''}<span class="product-card__title-text" data-product-card-title>${this.escapeHtml(item.title)}</span></a>
        ${item.volume ? `<div class="product-card__meta">${this.escapeHtml(item.volume)}</div>` : ''}
        <div class="product-card__price" data-discount-engine-price>
          ${item.price ? `<span class="price">${this.escapeHtml(letoileWholeMoney(item.price))}</span>` : ''}
          ${compare}
        </div>
      </article>
    `;
  }

  removeLabel() {
    return document.querySelector('[data-wishlist-list]')?.getAttribute('data-remove-label') || '';
  }

  renderPage() {
    const listNode = document.querySelector('[data-wishlist-list]');
    if (!listNode) return;

    const items = [...this.items.values()].filter((item) => item.title && item.url);
    const emptyNode = document.querySelector('[data-wishlist-empty]');

    if (!items.length) {
      listNode.hidden = true;
      listNode.innerHTML = '';
      if (emptyNode) emptyNode.hidden = false;
      return;
    }

    listNode.innerHTML = items.map((item) => this.productCard(item)).join('');
    listNode.hidden = false;
    if (emptyNode) emptyNode.hidden = true;
  }

  notify(detail = {}) {
    const eventDetail = {
      ...detail,
      items: [...this.items.values()],
      storageKey: this.storageKey,
    };

    document.dispatchEvent(new CustomEvent('letoile:wishlist:change', { detail: eventDetail }));

    const adapter = window.LetoileWishlistSync;
    if (typeof adapter === 'function') {
      adapter(eventDetail);
    } else if (typeof adapter?.sync === 'function') {
      adapter.sync(eventDetail);
    }
  }

  escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[char]));
  }

  escapeAttribute(value) {
    return this.escapeHtml(value).replace(/`/g, '&#96;');
  }
}

new LetoileWishlist();

class LetoileProductVariants {
  constructor(root = document) {
    this.root = root;
    this.select = letoileElements(root, '[data-variant-select]')[0];
    this.mobileSubmit = letoileElements(root, '[data-add-to-cart-mobile]')[0];
    this.visualOptions = letoileElements(root, '[data-product-option-value]');
    this.variants = this.readVariants();

    this.bindMobileSubmit();
    this.bindVisualOptions();

    if (this.select && !this.select.dataset.variantSelectBound && this.variants.length) {
      this.bind();
      this.update();
    }
  }

  readVariants() {
    const node = letoileElements(this.root, '[data-product-variants-json]')[0];
    if (!node) return [];

    try {
      return JSON.parse(node.textContent);
    } catch (_error) {
      return [];
    }
  }

  bind() {
    this.select.dataset.variantSelectBound = 'true';
    this.select.addEventListener('change', () => this.update());
  }

  bindMobileSubmit() {
    if (!this.mobileSubmit || this.mobileSubmit.dataset.addToCartMobileBound) return;

    this.mobileSubmit.dataset.addToCartMobileBound = 'true';
    this.mobileSubmit.addEventListener('click', () => {
      const submit = letoileElements(this.root, '[data-add-to-cart]')[0];
      if (!submit || submit.disabled) return;
      submit.click();
    });
  }

  bindVisualOptions() {
    this.visualOptions.forEach((button) => {
      if (button.dataset.productOptionBound) return;
      button.dataset.productOptionBound = 'true';
      button.addEventListener('click', () => {
        const position = Number(button.dataset.productOptionPosition || 0);
        if (!position) return;

        letoileElements(this.root, `[data-product-option-position="${position}"]`).forEach((item) => {
          const selected = item === button;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
        this.setText(`[data-product-option-selected-position="${position}"]`, button.dataset.productOptionValue || '');

        const selectedOptions = [];
        letoileElements(this.root, '[data-product-option-value].is-selected').forEach((item) => {
          selectedOptions[Number(item.dataset.productOptionPosition || 0) - 1] = item.dataset.productOptionValue;
        });

        const variant = this.variants.find((item) => (
          Array.isArray(item.options)
          && item.options.every((value, index) => !selectedOptions[index] || selectedOptions[index] === value)
        ));

        if (!variant || !this.select) return;
        this.select.value = String(variant.id);
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  update() {
    const variant = this.variants.find((item) => String(item.id) === this.select.value);
    if (!variant) return;

    this.setText('[data-variant-price]', variant.price);
    this.setText('[data-variant-price-mobile]', variant.price);
    this.setText('[data-variant-compare-at-price]', variant.compareAtPrice || '');
    this.setHidden('[data-variant-compare-at-price]', !variant.compareAtPrice);
    this.setText('[data-variant-compare-at-price-mobile]', variant.compareAtPrice || '');
    this.setHidden('[data-variant-compare-at-price-mobile]', !variant.compareAtPrice);
    this.setText('[data-variant-sale-badge]', variant.saleLabel || '');
    this.setHidden('[data-variant-sale-badge]', !variant.compareAtPrice);
    const shopifyPriceState = variant.compareAtPrice ? 'sale' : 'standard';
    letoileElements(this.root, '[data-shopify-price-state]').forEach((node) => {
      node.dataset.shopifyPriceState = shopifyPriceState;
      node.dataset.priceState = shopifyPriceState;
    });
    this.setHidden('[data-add-to-cart-available]', !variant.available);
    this.setHidden('[data-add-to-cart-sold-out]', variant.available);
    this.setHidden('[data-add-to-cart-mobile-available]', !variant.available);
    this.setHidden('[data-add-to-cart-mobile-sold-out]', variant.available);
    const skuRow = letoileElements(this.root, '[data-variant-sku-row]')[0];
    const skuButton = letoileElements(this.root, '[data-copy-sku]')[0];
    const skuValue = variant.sku || '';
    this.setText('[data-variant-sku]', skuValue || skuRow?.dataset.emptyValue || '—');
    if (skuButton) {
      skuButton.dataset.copyValue = skuValue;
      skuButton.disabled = !skuValue;
      skuButton.setAttribute('aria-label', skuButton.dataset.copyLabel || '');
      skuButton.setAttribute('title', skuButton.dataset.copyLabel || '');
    }
    this.setText('[data-variant-availability]', variant.availabilityText || '');

    (variant.options || []).forEach((value, index) => {
      const position = index + 1;
      this.setText(`[data-product-option-selected-position="${position}"]`, value || '');
      letoileElements(this.root, `[data-product-option-position="${position}"]`).forEach((item) => {
        const selected = item.dataset.productOptionValue === value;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    });

    letoileElements(this.root, '[data-discount-engine-variant-id]').forEach((node) => {
      node.setAttribute('data-discount-engine-variant-id', variant.id || '');
    });

    const submit = letoileElements(this.root, '[data-add-to-cart]')[0];
    if (submit) submit.disabled = !variant.available;
    const mobileSubmit = letoileElements(this.root, '[data-add-to-cart-mobile]')[0];
    if (mobileSubmit) mobileSubmit.disabled = !variant.available;
    const mobileBuyNow = letoileElements(this.root, '[data-buy-now-mobile]')[0];
    if (mobileBuyNow) mobileBuyNow.disabled = !variant.available;

    letoileElements(this.root, '[data-integration-slot][data-variant-id]').forEach((node) => {
      node.setAttribute('data-variant-id', variant.id || '');
    });

    const productSection = letoileElements(this.root, '.le-native-pdp')[0];
    productSection?.dispatchEvent(new CustomEvent('letoile:product-media:select', {
      detail: { mediaId: variant.featuredMediaId },
    }));

    letoileElements(this.root, '[data-wishlist-toggle]').forEach((button) => {
      button.setAttribute('data-variant-id', variant.id || '');
      button.setAttribute('data-product-price', variant.price || '');
      button.setAttribute('data-product-compare-at-price', variant.compareAtPrice || '');
      button.setAttribute('data-product-volume', variant.volume || '');
    });
  }

  setText(selector, text) {
    const node = letoileElements(this.root, selector)[0];
    if (node) node.textContent = text;
  }

  setHidden(selector, hidden) {
    const node = letoileElements(this.root, selector)[0];
    if (node) node.hidden = hidden;
  }

}

new LetoileProductVariants();

class LetoileProductShare {
  constructor(root = document) {
    this.buttons = letoileElements(root, '[data-share-product]').filter((button) => !button.dataset.shareProductBound);
    this.bind();
  }

  bind() {
    this.buttons.forEach((button) => {
      button.dataset.shareProductBound = 'true';
      button.addEventListener('click', async () => {
        const url = button.dataset.shareUrl || window.location.href;
        try {
          if (navigator.share) {
            await navigator.share({ title: document.title, url });
            return;
          }
          await navigator.clipboard.writeText(url);
        } catch (_error) {
          // Cancellation and clipboard permission failures are intentionally silent.
        }
      });
    });
  }
}

new LetoileProductShare();

class LetoileProductMemory {
  constructor(root = document, storage = letoileStorage()) {
    this.root = root;
    this.storage = storage;
    this.storageKey = 'letoile:recently-viewed';
    this.maxItems = 12;
    this.current = this.readCurrentProduct();
    this.captureCurrentProduct();
    this.renderRecentlyViewed();
    this.bindRecentlyViewedClear();
    this.loadRecommendations();
    this.loadProductRailRecommendations();
  }

  readCurrentProduct() {
    const node = letoileElements(this.root, '[data-current-product-json]')[0];
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (_error) {
      return null;
    }
  }

  readList() {
    if (!this.storage) return [];

    try {
      return JSON.parse(this.storage.getItem(this.storageKey) || '[]');
    } catch (_error) {
      return [];
    }
  }

  writeList(items) {
    if (!this.storage) return;
    try {
      this.storage.setItem(this.storageKey, JSON.stringify(items.slice(0, this.maxItems)));
    } catch (_error) {
      this.storage = null;
    }
  }

  captureCurrentProduct() {
    if (!this.current?.handle) return;

    const next = [
      this.current,
      ...this.readList().filter((item) => item.handle !== this.current.handle),
    ];

    this.writeList(next);
  }

  productCard(item) {
    const soldOutLabel = letoileElements(this.root, '[data-recently-viewed-section]')[0]
      ?.getAttribute('data-sold-out-label') || 'Sold out';
    const imageUrls = Array.isArray(item.images) && item.images.length
      ? item.images.filter(Boolean).slice(0, 3)
      : (item.image ? [item.image] : []);
    const images = imageUrls.map((imageUrl, imageIndex) => `
      <span class="le-product-card__image${imageIndex === 0 ? ' is-active' : ''}" data-product-card-image="${imageIndex}"${imageIndex === 0 ? '' : ' aria-hidden="true"'}>
        <img src="${this.escapeAttribute(imageUrl)}" alt="${this.escapeAttribute(item.title)}" loading="lazy">
      </span>
    `).join('');
    const image = imageUrls.length
      ? `
        <a class="le-product-card__image-link" href="${this.escapeAttribute(item.url)}" aria-label="${this.escapeAttribute(item.title)}" data-product-card-gallery data-product-card-image-count="${imageUrls.length}">
          ${images}
        </a>
      `
      : `<a class="le-product-card__image-link" href="${this.escapeAttribute(item.url)}" aria-label="${this.escapeAttribute(item.title)}"></a>`;
    const dots = imageUrls.length > 1
      ? `
        <span class="le-product-card__dots" aria-hidden="true">
          ${imageUrls.map((_, imageIndex) => `<i${imageIndex === 0 ? ' class="is-active"' : ''} data-product-card-dot="${imageIndex}"></i>`).join('')}
        </span>
      `
      : '';
    const compare = item.compareAtPrice
      ? `<span class="price--compare">${this.escapeHtml(letoileWholeMoney(item.compareAtPrice))}</span>`
      : '';
    const quickAdd = item.variantId && item.available === true
      ? `
        <form action="/cart/add" method="post" class="le-product-card__quick-add" data-product-card-quick-add>
          <input type="hidden" name="id" value="${this.escapeAttribute(item.variantId)}">
          <input type="hidden" name="quantity" value="1">
          <button
            class="button button--secondary"
            type="submit"
            data-product-card-quick-add-button
            data-idle-label="${this.escapeAttribute(item.quickAddLabel || 'Add to cart')}"
            data-loading-label="${this.escapeAttribute(item.quickAddLoadingLabel || 'Adding to cart')}"
            data-in-cart-label="${this.escapeAttribute(item.quickAddInCartLabel || 'In cart')}"
            data-error-label="${this.escapeAttribute(item.quickAddErrorLabel || 'Unable to add to cart')}"
            aria-pressed="false"
            aria-label="${this.escapeAttribute(item.quickAddAriaLabel || `${item.quickAddLabel || 'Add to cart'}: ${item.title}`)}"
          >
            <span class="le-product-card__quick-add-icon" data-product-card-quick-add-icon aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M11 19H7C5.89543 19 5 18.1046 5 17V7L7.5 4H16.5L19 7V11M19 7H5M15 19H23M19 15V23M9 10C9 11.6569 10.3431 13 12 13C13.6569 13 15 11.6569 15 10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <span class="visually-hidden le-product-card__quick-add-label" data-product-card-quick-add-label>${this.escapeHtml(item.quickAddLabel || 'Add to cart')}</span>
          </button>
        </form>
      `
      : '';

    return `
      <article class="le-product-card${item.available === false ? ' le-product-card--sold-out' : ''}" data-le-product-card data-discount-engine-context="product-card" data-discount-engine-product-id="${this.escapeAttribute(item.productId || '')}" data-discount-engine-product-handle="${this.escapeAttribute(item.handle || '')}" data-discount-engine-variant-id="${this.escapeAttribute(item.variantId || '')}" data-product-card-root>
        <div class="le-product-card__media">
          ${image}
          ${dots}
          ${quickAdd}
        </div>
        <div class="le-product-card__content">
          ${item.available === false ? '' : `
            <div class="le-product-card__price" data-discount-engine-price>
              <span class="price">${this.escapeHtml(letoileWholeMoney(item.price))}</span>
              ${compare}
            </div>
          `}
          <a class="le-product-card__title" href="${this.escapeAttribute(item.url)}" data-product-card-identity>${item.brand ? `<span class="le-product-card__brand" data-product-card-brand>${this.escapeHtml(item.brand)}</span> ` : ''}<span class="le-product-card__title-text" data-product-card-title>${this.escapeHtml(item.title)}</span></a>
          ${item.volume ? `<p class="le-product-card__variant">${this.escapeHtml(item.volume)}</p>` : ''}
          ${item.available === false ? `<p class="le-product-card__stock le-product-card__stock--muted">${this.escapeHtml(soldOutLabel)}</p>` : ''}
        </div>
      </article>
    `;
  }

  wholeMoney(value) {
    return letoileWholeMoney(value);
  }

  async renderRecentlyViewed() {
    const listNode = letoileElements(this.root, '[data-recently-viewed-list]')[0];
    if (!listNode) return;

    const limit = Number.parseInt(listNode.getAttribute('data-limit') || '8', 10);
    const storedItems = this.readList();
    const hydratedItems = await window.LetoileRecentProducts?.hydrate(storedItems) || storedItems;
    this.writeList(hydratedItems);
    const items = hydratedItems.filter((item) => item.handle && item.handle !== this.current?.handle);
    if (!items.length) return;

    listNode.innerHTML = items
      .slice(0, Number.isFinite(limit) ? limit : 8)
      .map((item) => this.productCard(item))
      .join('');
    listNode.hidden = false;
    listNode.closest('[data-recently-viewed-auto-visibility]')?.removeAttribute('hidden');
    letoileElements(this.root, '[data-recently-viewed-placeholder]')[0]?.remove();
  }

  bindRecentlyViewedClear() {
    letoileElements(this.root, '[data-recently-viewed-clear]').forEach((button) => {
      if (button.dataset.recentlyViewedClearBound === 'true') return;

      button.dataset.recentlyViewedClearBound = 'true';
      button.addEventListener('click', () => {
        this.writeList([]);
        const section = button.closest('[data-recently-viewed-section]');
        const list = section?.querySelector('[data-recently-viewed-list]');
        if (list) {
          list.replaceChildren();
          list.hidden = true;
        }
        if (section?.hasAttribute('data-recently-viewed-auto-visibility')) {
          section.hidden = true;
        }
      });
    });
  }

  async loadRecommendations() {
    const section = letoileElements(this.root, '[data-product-recommendations]')[0];
    if (!section) return;
    if (section.dataset.recommendationsLoaded === 'true' || section.dataset.recommendationsLoading === 'true') return;

    const sectionId = section.getAttribute('data-section-id');
    const productId = section.getAttribute('data-product-id');
    const limit = section.getAttribute('data-limit') || '8';
    const intent = section.getAttribute('data-intent') || 'related';
    const endpoint = section.getAttribute('data-recommendations-endpoint') || `${window.Shopify?.routes?.root || '/'}recommendations/products`;
    if (!sectionId || !productId) return;

    section.dataset.recommendationsLoading = 'true';
    const url = `${endpoint}?section_id=${encodeURIComponent(sectionId)}&product_id=${encodeURIComponent(productId)}&limit=${encodeURIComponent(limit)}&intent=${encodeURIComponent(intent)}`;

    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const next = html.querySelector('[data-product-recommendations]');
      if (next?.querySelector('[data-product-recommendations-list]')) {
        await this.waitForRecommendationIdle(section);
        if (!section.isConnected) return;
        next.dataset.recommendationsLoaded = 'true';
        section.replaceWith(next);
        initializeLetoileSection(next);
        return;
      }
    } catch (_error) {
      // Keep the server-rendered empty state when recommendations cannot load.
    }
    section.dataset.recommendationsLoading = 'false';
    section.dataset.recommendationsLoaded = 'true';
  }

  waitForRecommendationIdle(section) {
    if (!section.matches(':hover, :focus-within')) return Promise.resolve();

    return new Promise((resolve) => {
      const finishWhenIdle = () => {
        if (section.matches(':hover, :focus-within')) return;
        section.removeEventListener('pointerleave', finishWhenIdle);
        section.removeEventListener('focusout', finishWhenIdle);
        resolve();
      };
      section.addEventListener('pointerleave', finishWhenIdle);
      section.addEventListener('focusout', finishWhenIdle);
    });
  }

  async loadProductRailRecommendations() {
    const rails = letoileElements(this.root, '[data-product-rail-recommendations]');
    if (!rails.length) return;

    rails.forEach((rail) => this.loadRecommendationRail(rail));
  }

  async loadRecommendationRail(rail) {
    const sectionId = rail.getAttribute('data-section-id');
    const productId = rail.getAttribute('data-product-id');
    const limit = rail.getAttribute('data-limit') || '8';
    const intent = rail.getAttribute('data-intent') || 'related';
    if (!sectionId || !productId) return;

    const url = `${window.Shopify?.routes?.root || '/'}recommendations/products?section_id=${encodeURIComponent(sectionId)}&product_id=${encodeURIComponent(productId)}&limit=${encodeURIComponent(limit)}&intent=${encodeURIComponent(intent)}`;

    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const nextRail = html.querySelector('[data-product-rail-recommendations]');
      const grid = html.querySelector('[data-product-rail-list]');
      if (grid) {
        rail.replaceWith(grid);
        initializeLetoileSection(grid);
      } else if (nextRail?.querySelector('[data-product-rail-list]')) {
        const nextGrid = nextRail.querySelector('[data-product-rail-list]');
        rail.replaceWith(nextGrid);
        initializeLetoileSection(nextGrid);
      }
    } catch (_error) {
      // Keep the configured empty state when recommendations cannot load.
    }
  }

  escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[char]));
  }

  escapeAttribute(value) {
    return this.escapeHtml(value).replace(/`/g, '&#96;');
  }
}

new LetoileProductMemory();

class LetoileCookieConsent {
  constructor(root = document, storage = letoileStorage()) {
    this.storage = storage;
    this.storageKey = 'letoile:cookie-consent';
    this.banner = letoileElements(root, '[data-cookie-consent]')[0];
    if (!this.banner || this.banner.dataset.cookieConsentBound) return;

    this.button = this.banner.querySelector('[data-cookie-accept]');
    this.bind();
    this.showWhenNeeded();
  }

  bind() {
    this.banner.dataset.cookieConsentBound = 'true';
    this.button?.addEventListener('click', () => {
      try {
        this.storage?.setItem(this.storageKey, 'accepted');
      } catch (_error) {
        this.storage = null;
      }
      this.setShopifyConsent();
      this.banner.hidden = true;
    });
  }

  showWhenNeeded() {
    try {
      if (this.storage?.getItem(this.storageKey) === 'accepted') return;
    } catch (_error) {
      this.storage = null;
    }
    this.banner.hidden = false;
  }

  setShopifyConsent() {
    const api = window.Shopify?.customerPrivacy;
    if (!api?.setTrackingConsent) return;

    try {
      api.setTrackingConsent(true, () => {});
    } catch (_error) {
      // External CMP apps can own consent state; the fallback banner remains non-blocking.
    }
  }
}

new LetoileCookieConsent();


class LetoileSearchLoading {
  constructor(root = document) {
    this.forms = letoileElements(root, '[data-search-page-form]').filter((form) => !form.dataset.searchLoadingBound);
    if (!this.forms.length) return;

    this.bind();
  }

  bind() {
    this.forms.forEach((form) => {
      form.dataset.searchLoadingBound = 'true';
      form.addEventListener('submit', () => {
        const button = form.querySelector('[data-search-submit]');
        const label = form.querySelector('[data-search-submit-label]');
        const loading = form.querySelector('[data-search-loading-label]');
        if (!button || !loading) return;

        form.setAttribute('aria-busy', 'true');
        button.disabled = true;
        if (label) label.hidden = true;
        loading.hidden = false;
      });
    });
  }
}

new LetoileSearchLoading();

class LetoileCartLoading {
  constructor(root = document) {
    this.forms = letoileElements(root, '[data-cart-form]').filter((form) => !form.dataset.cartLoadingBound);
    if (!this.forms.length) return;

    this.bind();
  }

  bind() {
    this.forms.forEach((form) => {
      form.dataset.cartLoadingBound = 'true';
      form.addEventListener('submit', (event) => {
        if (event.submitter?.name === 'checkout') return;

        const button = form.querySelector('[data-cart-update-submit]');
        const label = form.querySelector('[data-cart-update-label]');
        const loading = form.querySelector('[data-cart-updating-label]');
        if (!button || !loading) return;

        form.setAttribute('aria-busy', 'true');
        button.disabled = true;
        if (label) label.hidden = true;
        loading.hidden = false;
      });
    });
  }
}

new LetoileCartLoading();

class LetoileCartQuantity {
  constructor(root = document) {
    this.forms = letoileElements(root, '[data-cart-form]').filter((form) => !form.dataset.cartQuantityBound);
    if (!this.forms.length) return;
    this.bind();
  }

  bind() {
    this.forms.forEach((form) => {
      form.dataset.cartQuantityBound = 'true';
      let submitTimer;

      const submitUpdate = () => {
        window.clearTimeout(submitTimer);
        submitTimer = window.setTimeout(() => {
          const updateButton = form.querySelector('[data-cart-update-submit]');
          if (updateButton && !updateButton.disabled) form.requestSubmit(updateButton);
        }, 350);
      };

      form.addEventListener('click', (event) => {
        const button = event.target.closest('[data-cart-quantity-adjust]');
        if (!button || !form.contains(button)) return;

        const input = button.closest('[data-cart-quantity-control]')?.querySelector('[data-cart-line-quantity]');
        if (!input) return;

        const step = Number(button.dataset.cartQuantityAdjust || 0);
        const minimum = Number(input.min || 0);
        const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
        const nextValue = Math.min(maximum, Math.max(minimum, Number(input.value || 0) + step));
        if (nextValue === Number(input.value)) return;

        input.value = String(nextValue);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      form.addEventListener('change', (event) => {
        if (event.target.matches('[data-cart-line-quantity]')) submitUpdate();
      });
    });
  }
}

new LetoileCartQuantity();

class LetoileReducedMotionMedia {
  constructor(root = document) {
    this.mediaBlocks = letoileElements(root, '[data-autoplay-media]').filter((block) => !block.dataset.reducedMotionMediaBound);
    if (!this.mediaBlocks.length) return;

    this.mediaBlocks.forEach((block) => {
      block.dataset.reducedMotionMediaBound = 'true';
    });
    this.media = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.sync = this.sync.bind(this);
    this.sync();
    this.media.addEventListener('change', this.sync);
  }

  sync() {
    this.mediaBlocks.forEach((block) => {
      const video = block.querySelector('video');
      if (!video) return;

      if (this.media.matches) {
        video.pause();
        video.removeAttribute('autoplay');
      } else {
        video.setAttribute('autoplay', '');
        video.play().catch(() => {});
      }
    });
  }
}

new LetoileReducedMotionMedia();

class LetoileResponsiveFacets {
  constructor(root = document) {
    this.facets = letoileElements(root, '[data-responsive-facets]').filter((facet) => !facet.dataset.responsiveFacetsBound);
    if (!this.facets.length) return;

    this.facets.forEach((facet) => {
      facet.dataset.responsiveFacetsBound = 'true';
    });
    this.media = window.matchMedia('(max-width: 989px)');
    this.sync = this.sync.bind(this);
    this.sync();
    this.media.addEventListener('change', this.sync);
  }

  sync() {
    this.facets.forEach((facet) => {
      if (this.media.matches) {
        facet.removeAttribute('open');
      } else {
        facet.setAttribute('open', '');
      }
    });
  }
}

new LetoileResponsiveFacets();

function initializeLetoileSection(root = document) {
  window.LetoileIntegrationSlots?.scan(root);
  window.LetoileWishlist?.sync();
  window.LetoileWishlist?.renderPage();
  new LetoileProductVariants(root);
  new LetoileProductMemory(root);
  new LetoileCookieConsent(root);
  new LetoileSearchLoading(root);
  new LetoileCartLoading(root);
  new LetoileScrollRails(root);
  new LetoileHomeTabs(root);
  new LetoileReducedMotionMedia(root);
  new LetoileResponsiveFacets(root);
}

document.addEventListener('shopify:section:load', (event) => initializeLetoileSection(event.target));
